import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as docsApi from '../../api/documents';
import AdminLayout from './AdminLayout';
import styles from './AdminDocuments.module.css';

const STATUS_LABELS = { draft: 'Черновик', published: 'Опубликован', archived: 'Архив' };

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const getFileTypeLabel = (type) => {
  if (type === 'application/pdf') return 'PDF';
  if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'DOCX';
  if (type === 'application/msword') return 'DOC';
  return 'Файл';
};

const EMPTY_FORM = {
  title: '',
  description: '',
  status: 'draft',
};

const AdminDocuments = () => {
  const { token } = useAuth();
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formMessage, setFormMessage] = useState({ text: '', type: '' });

  const [docFile, setDocFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await docsApi.getAllDocumentsAdmin(token);
      setDocs(data.documents);
    } catch {
      setError('Ошибка загрузки документов');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openCreate = () => {
    setEditingDoc(null);
    setForm(EMPTY_FORM);
    setDocFile(null);
    setFileName('');
    setFormMessage({ text: '', type: '' });
    setShowForm(true);
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setForm({
      title: doc.title,
      description: doc.description || '',
      status: doc.status,
    });
    setDocFile(null);
    setFileName(doc.file_name);
    setFormMessage({ text: '', type: '' });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingDoc(null);
    setDocFile(null);
    setFileName('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setDocFile(file);
    setFileName(file.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setFormMessage({ text: 'Укажите название документа', type: 'error' });
      return;
    }
    if (!editingDoc && !docFile) {
      setFormMessage({ text: 'Загрузите файл документа', type: 'error' });
      return;
    }

    setIsSaving(true);
    setFormMessage({ text: '', type: '' });

    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('status', form.status);
    if (docFile) fd.append('file', docFile);

    try {
      if (editingDoc) {
        await docsApi.updateDocument(token, editingDoc.id, fd);
        setFormMessage({ text: 'Документ обновлён', type: 'success' });
      } else {
        await docsApi.createDocument(token, fd);
        setFormMessage({ text: 'Документ создан', type: 'success' });
      }
      fetchDocs();
      setTimeout(closeForm, 1000);
    } catch (err) {
      setFormMessage({ text: err.response?.data?.message || 'Ошибка сохранения', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить документ? Это действие нельзя отменить.')) return;
    try {
      await docsApi.deleteDocument(token, id);
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch {
      alert('Ошибка при удалении');
    }
  };

  return (
    <AdminLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>Документы</h1>
          <button className={styles.createBtn} onClick={openCreate}>
            + Добавить документ
          </button>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {showForm && (
          <div className={styles.formWrap}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>
                {editingDoc ? 'Редактировать документ' : 'Новый документ'}
              </h2>
              <button className={styles.closeBtn} onClick={closeForm}>✕</button>
            </div>

            {formMessage.text && (
              <div className={`${styles.formMessage} ${styles[formMessage.type]}`}>
                {formMessage.text}
              </div>
            )}

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>Название *</label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Например: Публичная оферта"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Описание</label>
                <input
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Краткое описание документа"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Файл {editingDoc ? '(загрузите для замены)' : '*'}</label>
                <div className={styles.fileUpload}>
                  {fileName && (
                    <span className={styles.currentFile}>{fileName}</span>
                  )}
                  <label className={styles.uploadBtn} htmlFor="doc-file">
                    {fileName ? 'Заменить файл' : 'Выбрать файл'}
                  </label>
                  <input
                    id="doc-file"
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileChange}
                    className={styles.fileInput}
                  />
                  <span className={styles.fileHint}>PDF, DOC, DOCX — до 20 МБ</span>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Статус</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleFormChange}
                  className={styles.input}
                >
                  <option value="draft">Черновик</option>
                  <option value="published">Опубликован</option>
                  <option value="archived">Архив</option>
                </select>
              </div>

              <div className={styles.formActions}>
                <button type="submit" className={styles.saveBtn} disabled={isSaving}>
                  {isSaving ? 'Сохранение...' : editingDoc ? 'Сохранить' : 'Создать'}
                </button>
                <button type="button" className={styles.cancelBtn} onClick={closeForm}>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        )}

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>Документ</th>
                <th className={styles.th}>Статус</th>
                <th className={styles.th}>Дата</th>
                <th className={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={4} className={styles.loadingCell}>Загрузка...</td></tr>
              ) : docs.length === 0 ? (
                <tr><td colSpan={4} className={styles.emptyCell}>Документов пока нет</td></tr>
              ) : (
                docs.map((doc) => (
                  <tr key={doc.id} className={styles.row}>
                    <td className={styles.td}>
                      <span className={styles.docTitle}>{doc.title}</span>
                      <span className={styles.docMeta}>
                        <span className={styles.typeBadge}>{getFileTypeLabel(doc.file_type)}</span>
                        {formatFileSize(doc.file_size)}
                      </span>
                      {doc.description && (
                        <span className={styles.docDesc}>
                          {doc.description.slice(0, 80)}{doc.description.length > 80 ? '...' : ''}
                        </span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <span className={`${styles.statusBadge} ${styles[`status_${doc.status}`]}`}>
                        {STATUS_LABELS[doc.status]}
                      </span>
                    </td>
                    <td className={styles.td}>{formatDate(doc.created_at)}</td>
                    <td className={styles.td}>
                      <div className={styles.rowActions}>
                        <button className={styles.editBtn} onClick={() => openEdit(doc)}>Изменить</button>
                        <button className={styles.deleteBtn} onClick={() => handleDelete(doc.id)}>Удалить</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDocuments;
